import {
  directedStylePreviewKey,
  type DirectedHistoryEntry,
  type DirectedStory,
  type DirectedStyle,
  type SerializedDirectedVideo,
} from 'fym-shared/src/music24/directedVideoApi';

/** One round of a director step: the options it offered, the note of the artist, and when. */
export interface DirectedRound<T> {
  key: string;
  options: T[];
  note: string | null;
  at: string | null;
}

/**
 * The rounds of the story step (design directed-video l1), oldest first: one per `stories` entry of
 * the history. A video from before the history (no entries) shows its last set as one round. Pure.
 */
export function storyRounds(
  entries: readonly DirectedHistoryEntry[],
  video: Pick<SerializedDirectedVideo, 'stories'>
): DirectedRound<DirectedStory>[] {
  const rounds = entries.flatMap((entry) =>
    entry.kind === 'stories' && entry.stories !== undefined && entry.stories.length > 0
      ? [{ key: `round-${entry.id}`, options: entry.stories, note: entry.note, at: entry.at }]
      : []
  );
  if (rounds.length > 0) {
    return rounds;
  }
  return video.stories === null || video.stories.length === 0
    ? []
    : [{ key: 'last', options: video.stories, note: null, at: null }];
}

/** One round of the style step, with the ready preview still of each style of the round, by style id. */
export interface DirectedStyleRound extends DirectedRound<DirectedStyle> {
  previewUrls: Record<string, string>;
}

/** The ready preview stills of the video (`stylePreviews`), by style id. Pure. */
function readyPreviewUrls(video: Pick<SerializedDirectedVideo, 'stylePreviews'>): Record<string, string> {
  const urls: Record<string, string> = {};
  for (const preview of video.stylePreviews ?? []) {
    if (preview.status === 'ready' && preview.imageUrl !== null) {
      urls[preview.styleId] = preview.imageUrl;
    }
  }
  return urls;
}

/**
 * The rounds of the style step, like `storyRounds`. Each round keeps the preview stills that the API
 * matched to the look of its styles (`stylePreviewUrls`). A video from before the history takes the
 * previews of `stylePreviews`. Pure.
 */
export function styleRounds(
  entries: readonly DirectedHistoryEntry[],
  video: Pick<SerializedDirectedVideo, 'styles' | 'stylePreviews'>
): DirectedStyleRound[] {
  const rounds = entries.flatMap((entry) =>
    entry.kind === 'styles' && entry.styles !== undefined && entry.styles.length > 0
      ? [
          {
            key: `round-${entry.id}`,
            options: entry.styles,
            note: entry.note,
            at: entry.at,
            previewUrls: entry.stylePreviewUrls ?? {},
          },
        ]
      : []
  );
  if (rounds.length > 0) {
    return rounds;
  }
  return video.styles === null || video.styles.length === 0
    ? []
    : [{ key: 'last', options: video.styles, note: null, at: null, previewUrls: readyPreviewUrls(video) }];
}

/**
 * The round that holds the pick: the newest round with an option of the picked id, else the newest
 * round. -1 with no rounds. Pure.
 */
export function pickedRoundIndex<T extends { id: string }>(
  rounds: readonly DirectedRound<T>[],
  picked: { id: string } | null
): number {
  for (let index = rounds.length - 1; index >= 0; index -= 1) {
    if (picked !== null && rounds[index]?.options.some((option) => option.id === picked.id) === true) {
      return index;
    }
  }
  return rounds.length - 1;
}

/** True when `option` is the picked one (the same id). Pure. */
export function isPickedOption(option: { id: string }, picked: { id: string } | null): boolean {
  return picked !== null && option.id === picked.id;
}

/** True when the artist changed the picked option after the pick (its text differs). Pure. */
export function isEditedPick(
  option: DirectedStory | DirectedStyle,
  picked: DirectedStory | DirectedStyle | null
): boolean {
  if (picked === null || option.id !== picked.id) {
    return false;
  }
  const { castFit: _a, castFitReason: _b, ...offered } = option as DirectedStyle;
  const { castFit: _c, castFitReason: _d, ...chosen } = picked as DirectedStyle;
  return JSON.stringify(offered) !== JSON.stringify(chosen);
}

/** The changes of the script in the history: the edits of the artist and the shot rewrites. Pure. */
export function scriptChanges(entries: readonly DirectedHistoryEntry[]): { saves: number; rewrites: number } {
  return {
    saves: entries.filter((entry) => entry.kind === 'scriptSave').length,
    rewrites: entries.filter((entry) => entry.kind === 'shot').length,
  };
}

/** The time of the confirm (the make started), or null. Pure. */
export function confirmedAt(entries: readonly DirectedHistoryEntry[]): string | null {
  return entries.find((entry) => entry.kind === 'confirm')?.at ?? null;
}

/** True when two styles have the same look texts (the look part of `directedStylePreviewKey`). Pure. */
function sameLook(a: DirectedStyle, b: DirectedStyle): boolean {
  return directedStylePreviewKey(null, a) === directedStylePreviewKey(null, b);
}

/**
 * The preview still of one style card of "How it was made", or null (no picture box).
 *
 * Style ids repeat over the rounds (`style-1` in each round), so the id alone is not enough: a
 * picture shows only for the look that it was made for. The API matches each history round by the
 * look key (story + look texts) and sends `stylePreviewUrls` for it. The picked card shows the
 * picked (maybe edited) style: its still comes from `stylePreviews` (the look of now), else from a
 * `stylePick` entry of the same look, else from the round when the artist did not edit the pick.
 * Pure.
 */
export function howMadeStylePreviewUrl(
  option: DirectedStyle,
  round: DirectedStyleRound | undefined,
  video: Pick<SerializedDirectedVideo, 'style' | 'stylePreviews'>,
  entries: readonly DirectedHistoryEntry[]
): string | null {
  const picked = video.style;
  if (picked === null || !isPickedOption(option, picked)) {
    return round?.previewUrls[option.id] ?? null;
  }
  const current = readyPreviewUrls(video)[picked.id];
  if (current !== undefined) {
    return current;
  }
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    const url = entry?.stylePreviewUrls?.[picked.id];
    if (
      entry?.kind === 'stylePick' &&
      entry.style !== undefined &&
      entry.style !== null &&
      url !== undefined &&
      sameLook(entry.style, picked)
    ) {
      return url;
    }
  }
  return isEditedPick(option, picked) ? null : (round?.previewUrls[option.id] ?? null);
}
